/**
 * Message Compose Store
 *
 * Global state management for the Message Compose Dialog.
 * Supports both email and WhatsApp channels.
 * Allows triggering the dialog from anywhere in the app.
 */

import { create } from 'zustand';
import type { CustomerEmailData, EmailContext } from '@/utils/email';

/**
 * Message channel type
 * - email: Opens email client (Gmail, Outlook, mailto, etc.)
 * - whatsapp: Opens WhatsApp Web/app with prefilled message
 */
export type MessageChannel = 'email' | 'whatsapp';

export interface MessageComposePayload {
  /** Message channel (default: 'email') */
  channel?: MessageChannel;
  /** Customer data with email and name fields */
  customer: CustomerEmailData;
  /** Context determines which templates are available */
  context: EmailContext;
  /** Work order ID (for workOrder context) */
  workOrderId?: string;
  /** Invoice number (for invoice context) */
  invoiceNumber?: string;
  /** License plate (for workOrder/appointment context) */
  licensePlate?: string;
  /** Appointment date (for appointment context) */
  appointmentDate?: string;

  // Smart defaults support
  /** Explicit default template ID override */
  defaultTemplateId?: string;
  /** Invoice status for smart defaults (e.g., 'overdue') */
  invoiceStatus?: string;
  /** Work order status for smart defaults (e.g., 'DONE') */
  workOrderStatus?: string;

  // Company/BV for email closing
  /** Company profile ID for invoice context (to get BV name) */
  companyProfileId?: string;

  // PDF download URL (for invoice context with PDF link)
  /** PDF download URL to include in email body */
  pdfDownloadUrl?: string;

  // Callback when message is actually sent/opened
  /** Called when user clicks "Open e-mail" or "Open WhatsApp" (not just opening the drawer) */
  onSend?: () => void;
}

interface MessageComposeStore {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Current payload (customer, context, etc.) */
  payload: MessageComposePayload | null;
  /** Open the dialog with the given payload */
  open: (payload: MessageComposePayload) => void;
  /** Close the dialog and clear payload */
  close: () => void;
}

/**
 * Message Compose Store
 *
 * Usage:
 * ```tsx
 * const { open } = useMessageComposeStore();
 *
 * // Open dialog for email
 * open({ channel: 'email', customer, context: 'customer' });
 *
 * // Open dialog for WhatsApp
 * open({ channel: 'whatsapp', customer, context: 'workOrder', workOrderId: '123' });
 * ```
 */
export const useMessageComposeStore = create<MessageComposeStore>((set) => ({
  isOpen: false,
  payload: null,
  open: (payload) => set({ isOpen: true, payload }),
  close: () => set({ isOpen: false, payload: null }),
}));

/** @deprecated Use useMessageComposeStore instead */
export const useEmailComposeStore = useMessageComposeStore;

/** @deprecated Use MessageComposePayload instead */
export type EmailComposePayload = MessageComposePayload;
